
import React, { useState, Suspense, lazy, useEffect } from 'react';
import Header from './components/Header';
import { Loader2 } from 'lucide-react';
import { Page } from './types';

// Lazy load views for better performance
const HomeView = lazy(() => import('./views/HomeView'));
const ServicesView = lazy(() => import('./views/ServicesView'));
const PricingView = lazy(() => import('./views/PricingView'));
const AboutView = lazy(() => import('./views/AboutView'));
const ContactView = lazy(() => import('./views/ContactView'));
const DashboardView = lazy(() => import('./views/DashboardView'));
const PrivacyPolicyView = lazy(() => import('./views/PrivacyPolicyView'));
const TermsView = lazy(() => import('./views/TermsView'));

// Lazy load heavy global components
const Footer = lazy(() => import('./components/Footer'));
const SEOAnalysisModal = lazy(() => import('./components/SEOAnalysisModal'));
const WhatsAppChat = lazy(() => import('./components/WhatsAppChat'));

const PageLoader = () => (
  <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white/50 backdrop-blur-sm">
    <Loader2 className="animate-spin text-indigo-600 mb-4" size={40} />
    <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Laddar upplevelse...</p>
  </div>
);

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<string | undefined>(undefined);

  // SEO Dynamic Updates
  useEffect(() => {
    const pageTitles: Record<Page, string> = {
      home: 'Sökoptimering & SEO-byrå i Sverige | Sökoptimera.com',
      services: 'SEO-tjänster: On-page, Off-page & Länkstrategi | Sökoptimera.com',
      pricing: 'Prisvärda SEO-paket för alla företag | Sökoptimera.com',
      about: 'Om oss - Experter på Sökoptimering | Sökoptimera.com',
      contact: 'Kontakta oss för en gratis SEO-analys | Sökoptimera.com',
      dashboard: 'Kundportal - Sökoptimera.com',
      privacy: 'Integritetspolicy | Sökoptimera.com',
      terms: 'Användarvillkor | Sökoptimera.com'
    };

    const pageDescriptions: Record<Page, string> = {
      home: 'Sökoptimera.com är din expert på sökoptimering, On-page & Off-page SEO, länkstrategi och lokal SEO. Vi driver organisk tillväxt för företag som vill synas på Google.',
      services: 'Utforska våra SEO-tjänster. Vi erbjuder teknisk SEO, innehållsoptimering och strategiskt länkbygge för att öka din synlighet.',
      pricing: 'Se våra transparenta priser för SEO. Vi har paket som passar både små lokala företag och stora e-handlare.',
      about: 'Lär känna teamet bakom Sökoptimera.com och vår passion för att hjälpa företag att växa genom organisk sökning.',
      contact: 'Hör av dig till oss! Vi erbjuder en kostnadsfri SEO-analys för att visa hur vi kan förbättra din ranking.',
      dashboard: 'Logga in för att se din SEO-rapport och framsteg.',
      privacy: 'Läs om hur vi hanterar dina personuppgifter och värnar om din integritet.',
      terms: 'Våra villkor för användning av tjänsten.'
    };

    document.title = pageTitles[currentPage] || pageTitles.home;
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescriptions[currentPage] || pageDescriptions.home);
    }

    // Update Open Graph tags too
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', pageTitles[currentPage] || pageTitles.home);
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', pageDescriptions[currentPage] || pageDescriptions.home);

  }, [currentPage]);

  const navigate = (page: Page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const openModal = (packageName?: string) => {
    setSelectedPackage(packageName);
    setIsModalOpen(true);
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPackage(undefined);
  };

  const renderView = () => {
    switch (currentPage) {
      case 'home': return <HomeView onNavigate={navigate} onCTAClick={() => openModal()} />;
      case 'services': return <ServicesView onCTAClick={() => openModal()} />;
      case 'pricing': return <PricingView onSelectPackage={(pkg) => openModal(pkg)} />;
      case 'about': return <AboutView onNavigate={navigate} />;
      case 'contact': return <ContactView />;
      case 'dashboard': return <DashboardView onNavigate={navigate} />;
      case 'privacy': return <PrivacyPolicyView />;
      case 'terms': return <TermsView />;
      default: return <HomeView onNavigate={navigate} onCTAClick={() => openModal()} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {currentPage !== 'dashboard' && (
        <Header currentPage={currentPage} onNavigate={navigate} onContactClick={() => openModal()} />
      )}
      <main className="flex-grow">
        <Suspense fallback={<PageLoader />}>
          {renderView()}
        </Suspense>
      </main>
      <Suspense fallback={null}>
        {currentPage !== 'dashboard' && <Footer onNavigate={navigate} />}
        {currentPage !== 'dashboard' && <WhatsAppChat />}
        {isModalOpen && (
          <SEOAnalysisModal 
            isOpen={isModalOpen} 
            onClose={closeModal} 
            selectedPackage={selectedPackage} 
          />
        )}
      </Suspense>
    </div>
  );
};

export default App;
